// Panel de Configuración → Tipos de solución. CRUD del catálogo administrable
// de "cómo se resolvió" un ticket. Se referencia por FK (casos/imeis), así que
// renombrar aquí SÍ se refleja en todos los casos que lo usan.
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type TipoSolucion } from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

export default function TiposSolucionPanel() {
  const toast = useToast();
  const [items, setItems] = useState<TipoSolucion[]>([]);
  const [loading, setLoading] = useState(true);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [confirmDel, setConfirmDel] = useState<TipoSolucion | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<TipoSolucion[]>(
        "/api/tipos-solucion?incluir_inactivos=true",
      );
      setItems(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function crear(e: FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim()) return;
    setCreando(true);
    try {
      const t = await api.post<TipoSolucion>("/api/tipos-solucion", {
        nombre: nombreNuevo.trim(),
      });
      toast.success("Tipo de solución creado", t.nombre);
      setNombreNuevo("");
      await refresh();
    } catch (err) {
      toast.error("No se pudo crear", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(t: TipoSolucion) {
    try {
      await api.patch(`/api/tipos-solucion/${t.id}`, { activo: !t.activo });
      await refresh();
    } catch (err) {
      toast.error("No se pudo actualizar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  function startEdit(t: TipoSolucion) {
    setEditandoId(t.id);
    setEditNombre(t.nombre);
  }

  async function saveEdit(t: TipoSolucion) {
    if (!editNombre.trim() || editNombre === t.nombre) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch(`/api/tipos-solucion/${t.id}`, { nombre: editNombre.trim() });
      toast.success("Renombrado", "Se actualizó en todos los casos que lo usan");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      toast.error("No se pudo renombrar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  async function mover(t: TipoSolucion, dir: -1 | 1) {
    const idx = items.findIndex((x) => x.id === t.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const otra = items[swapIdx];
    try {
      await api.patch(`/api/tipos-solucion/${t.id}`, { orden: otra.orden });
      await api.patch(`/api/tipos-solucion/${otra.id}`, { orden: t.orden });
      await refresh();
    } catch (err) {
      toast.error("No se pudo reordenar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  async function eliminar(t: TipoSolucion) {
    try {
      await api.del(`/api/tipos-solucion/${t.id}`);
      toast.success("Eliminado", t.nombre);
      await refresh();
    } catch (err) {
      toast.error("No se pudo eliminar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  return (
    <>
      <form
        onSubmit={crear}
        className="card flex flex-col gap-3 sm:flex-row sm:items-end"
      >
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nuevo tipo de solución
          </label>
          <input
            type="text"
            className="input mt-1"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="ej. Reparación, Reemplazo, Cambio por garantía"
          />
        </div>
        <button
          type="submit"
          disabled={!nombreNuevo.trim() || creando}
          className="btn-primary disabled:opacity-50"
        >
          {creando ? "Creando..." : "+ Agregar"}
        </button>
      </form>

      <div className="card">
        <div className="text-xs text-zinc-500 mb-3">
          Estos tipos se eligen al cerrar un caso y alimentan las estadísticas
          de "cómo se resuelven los tickets". Renombrar aquí se refleja en todos
          los casos que lo usan. No se puede eliminar uno en uso: desactívalo
          para ocultarlo sin perder el histórico.
        </div>
        {loading ? (
          <SkeletonRows rows={6} cols={2} />
        ) : items.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay tipos de solución todavía. Agrega el primero arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {items.map((t, i) => (
              <li
                key={t.id}
                className={`py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${t.activo ? "" : "opacity-50"}`}
              >
                {editandoId === t.id ? (
                  <input
                    type="text"
                    autoFocus
                    className="input flex-1"
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                    onBlur={() => saveEdit(t)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(t);
                      if (e.key === "Escape") setEditandoId(null);
                    }}
                  />
                ) : (
                  <span className="flex-1 text-zinc-200">{t.nombre}</span>
                )}
                <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                  <button
                    onClick={() => mover(t, -1)}
                    aria-label={`Subir ${t.nombre}`}
                    disabled={i === 0}
                    className="btn-ghost text-xs disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => mover(t, 1)}
                    aria-label={`Bajar ${t.nombre}`}
                    disabled={i === items.length - 1}
                    className="btn-ghost text-xs disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button onClick={() => toggleActivo(t)} className="btn-ghost text-xs">
                    {t.activo ? "Desactivar" : "Activar"}
                  </button>
                  <button onClick={() => startEdit(t)} className="btn-ghost text-xs">
                    Renombrar
                  </button>
                  <button
                    onClick={() => setConfirmDel(t)}
                    className="btn-ghost text-xs text-red-400 hover:text-red-300"
                  >
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description="Si está en uso en algún caso o equipo no se podrá eliminar; en ese caso desactívalo. El histórico conserva su tipo."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </>
  );
}
